import React, { useEffect, useRef, useState } from 'react';
import {
  Wrapper,
  SelectBox,
  LeftContent,
  IconButton,
  Dropdown,
  SearchWrapper,
  SearchInput,
  Option,
  StyledPlaceholder,
  StyledSelected,
  LeftIcon,
  RightIcon,
} from './styled';
import { ReactComponent as DownIcon } from 'assets/icons/ui-elements/small/caret-down-sm.svg';
import { ReactComponent as UpIcon } from 'assets/icons/ui-elements/small/caret-up-sm.svg';
import { ReactComponent as XIcon } from 'assets/icons/ui-elements/small/x-mark-sm.svg';
import { ReactComponent as LupaIcon } from 'assets/icons/ui-elements/small/magnifying_glass_sm.svg';

export interface SelectOption {
  label: string;
  value: string | number | null;
}

export interface SelectDropdownProps {
  options: SelectOption[];
  onChange: (value: string | number | null) => void;
  value?: string | number | null;
  size?: 'sm' | 'md' | 'lg';
  iconLeft?: React.ReactNode | React.ReactNode[];
  iconRight?: React.ReactNode;
  searchable?: boolean;
  clearable?: boolean;
  placeholder?: string;
  width?: string;
  height?: string;
  isLeftIconBlack?: boolean;
  style?: React.CSSProperties;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  onChange,
  value,
  size = 'md',
  iconLeft,
  iconRight,
  searchable = false,
  clearable = false,
  placeholder,
  width = 'auto',
  height,
  isLeftIconBlack = false,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find(opt => opt.value === value);

  const filteredOptions = options.filter(opt => opt?.label?.toLowerCase()?.includes(query?.toLowerCase()));

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <Wrapper width={width} height={height} ref={dropdownRef} style={style}>
      <SelectBox size={size} onClick={() => setOpen(prev => !prev)} isOpen={open} width={width} height={height}>
        <LeftContent isLeftIconBlack={isLeftIconBlack}>
          {Array.isArray(iconLeft)
            ? iconLeft.map((Icon, idx) => <React.Fragment key={idx}>{Icon}</React.Fragment>)
            : iconLeft}
          {selected?.label ? (
            <StyledSelected>{selected.label}</StyledSelected>
          ) : (
            <StyledPlaceholder>{placeholder}</StyledPlaceholder>
          )}
        </LeftContent>
        <LeftContent>
          {clearable && selected ? (
            <IconButton onClick={handleClear}>
              <XIcon />
            </IconButton>
          ) : (
            <>
              {iconRight && iconRight}
              <IconButton>{open ? <UpIcon /> : <DownIcon />}</IconButton>
            </>
          )}
        </LeftContent>
      </SelectBox>
      {open && (
        <Dropdown isSearchable={searchable}>
          {searchable && (
            <SearchWrapper size={size}>
              <LeftIcon>
                <LupaIcon />
              </LeftIcon>
              <SearchInput placeholder="Buscar..." value={query} onChange={e => setQuery(e.target.value)} />
              <RightIcon onClick={() => setOpen(prev => !prev)}>
                <UpIcon />
              </RightIcon>
            </SearchWrapper>
          )}
          {filteredOptions.map(option => (
            <Option
              size={size}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
};
